// LocalHub — the in-page local-server world, shared across emulator tiles.
//
// A player's own machine can be the server for singleplayer (one tile) AND for
// split-screen-plus-remote (several couch tiles + remote friends in ONE game,
// docs/p2p.md). Both are the same thing at different N: one LocalServer running
// the launched set's SERVER halves, with clients — each couch tile's in-process
// loopback and each remote peer — sharing it, one slot apiece, through the ONE
// slot-aware sink router (PeerHost).
//
// That shared world lives here rather than on any single MunosScriptHost so that
// N tiles can join it. Each tile's supervisor holds a LocalHub reference:
//   - injected (main.ts hands every tile the SAME hub) → the tiles share one
//     server, so couch players are in one game and any of them can Share it;
//   - not injected (a lone supervisor, e.g. a test) → it lazily owns a private
//     hub, which is exactly the old per-tile behavior (singleplayer, N=1).
//
// Lifecycle: the server (and its router) come up lazily on the first local
// launch and are torn down by reset() — called at a fresh-game boundary (a ROM
// (re)load resets the shared hub; a lone supervisor resets its owned hub on
// stop). There is deliberately no refcount auto-teardown: each loopback is bound
// to the PeerHost instance alive when it was created, so a straggler from a
// previous generation closing can never disturb the current server's slots.

import { LocalServer } from "./local-server.js";
import { LoopbackBusSocket } from "./loopback-bus-socket.js";
import { PeerHost, acceptInfoOffer, type HostSessionInfo } from "./peer-host.js";
import { BrokerHostSession, type BrokerSignalDeps } from "./broker-signal.js";
import type { RtcHostPeerDeps } from "./rtc-host-peer.js";
import type { MunosKvBackend, MunosCompiler } from "@multinostalgia/munos";

export interface LocalHubOptions {
  // What max_clients() reports + the server's slot capacity. Matches the
  // supervisors' maxClients so scripts and the server agree.
  maxClients?: number;
  // Tick rate (Hz) for the in-page server's relay heartbeat. Omitted → the
  // LocalServer default (30 Hz).
  tickHz?: number;
  // WebRTC seam for the PeerHost that accepts remote joiners. Production omits
  // it (DOM defaults); headless tests inject node-datachannel's polyfill.
  peerDeps?: RtcHostPeerDeps;
  // Persistence backend override, forwarded to the LocalServer. Production omits
  // it (the shared OPFS store); headless tests inject an InMemoryMunosKv so they
  // never touch a Worker/OPFS. Mirrors LocalServerOptions.kv.
  kv?: MunosKvBackend;
  // Compiler backend for the in-page server's SERVER halves. Production wires
  // the Rust/WASM compiler here (main.ts loads it once at boot); omitted → the
  // LocalServer default (the in-process TypeScript reference).
  compiler?: MunosCompiler;
}

export class LocalHub {
  // The in-page server for the launched set's server halves, and the one
  // slot-aware sink router that fans its per-slot output to each connected
  // client (loopback tiles + remote peers). Both come up together, lazily.
  private server: LocalServer | null = null;
  private serverInit: Promise<LocalServer> | null = null;
  private peerHost: PeerHost | null = null;
  // The broker connection while sharing to remote friends; null when not.
  private brokerSession: BrokerHostSession | null = null;

  private readonly maxClients: number | undefined;
  private readonly tickHz: number | undefined;
  private readonly peerDeps: RtcHostPeerDeps | undefined;
  private readonly kv: MunosKvBackend | undefined;
  private readonly compiler: MunosCompiler | undefined;

  // Provider for the session metadata (mod URLs + ROM hash) advertised to a
  // joiner's session-info bootstrap (session-info.ts), since the share link no
  // longer carries them. Read LIVE (not a snapshot) each time a joiner asks, so
  // it reflects a lobby→game hop that re-points the running tenants AFTER load.
  // main.ts wires it to the primary host's live tenants + loaded ROM; the default
  // (before wiring, or in a hub with no host) advertises nothing.
  private sessionInfoProvider: () => HostSessionInfo = () => ({ mode: "p2p", modUrls: [], romHash: null });

  constructor(opts: LocalHubOptions = {}) {
    this.maxClients = opts.maxClients;
    this.tickHz = opts.tickHz;
    this.peerDeps = opts.peerDeps;
    this.kv = opts.kv;
    this.compiler = opts.compiler;
  }

  // Get-or-create the in-page server and its router. Memoized: concurrent local
  // launches (across tiles or within one) await the same KV-backed construction.
  // The router (PeerHost) is created with it and owns the server sink from the
  // outset, so a single tile is just the one-client case of the shared model.
  ensureServer(): Promise<LocalServer> {
    if (!this.serverInit) {
      this.serverInit = LocalServer.create({
        ...(this.maxClients !== undefined ? { maxClients: this.maxClients } : {}),
        ...(this.tickHz !== undefined ? { tickHz: this.tickHz } : {}),
        ...(this.kv !== undefined ? { kv: this.kv } : {}),
        ...(this.compiler !== undefined ? { compiler: this.compiler } : {}),
      }).then((server) => {
        this.server = server;
        this.peerHost = new PeerHost(server, {
          ...(this.peerDeps ? { peerDeps: this.peerDeps } : {}),
          sessionInfo: () => this.sessionInfoProvider(),
        });
        return server;
      });
    }
    return this.serverInit;
  }

  // Whether the server + router are up (ensureServer has resolved). The bus
  // factory checks this synchronously before handing out a loopback.
  get ready(): boolean {
    return this.server !== null && this.peerHost !== null;
  }

  // Host one launched mod's SERVER half on the shared server. Idempotent per
  // label, so several tiles launching the same set add the member set exactly
  // once (the server halves are shared; only the client halves are per-tile).
  // `initData` seeds the member's `event init` (the per-instance spawn payload
  // — a private Level Player mount passes the chosen level's blob, so the
  // hosted store is born with the level; docs/level-player-sessions.md §4).
  // Omitted ⇒ init with no seed (couch / host-locally mounts).
  addMember(source: string, label: string, scope: string, initData?: Uint8Array): void {
    this.server?.addMember(source, label, scope, initData);
  }

  // Wire the in-page server's parent uplink (docs/node-federation.md, Stage C).
  // Called when the supervisor acts on a host_locally instruction: the local
  // server keeps the remote instance that sent it as its uplink parent, so the
  // editor's server{} just send_up()s and names no address.
  adoptParent(url: string, handoff: Uint8Array): void {
    this.server?.adoptParent(url, handoff);
  }

  // A managed loopback for one tile's local bus: it allocates its own slot on
  // the shared server through the router (so N couch tiles coexist), and is
  // BOUND to the router instance alive right now — a later reset() swaps in a
  // fresh router, and this socket keeps talking only to its own generation's,
  // which is harmless once that generation is gone.
  createLocalSocket(): LoopbackBusSocket {
    const peerHost = this.peerHost;
    const server = this.server;
    if (!peerHost || !server) {
      throw new Error("LocalHub.createLocalSocket before ensureServer");
    }
    return new LoopbackBusSocket(server, {
      attach: (self) => peerHost.attachLocal(self),
      detach: (slot) => peerHost.detachLocal(slot),
    });
  }

  // ----- P2P hosting (docs/p2p.md) -----

  // Wire the live source of the session metadata (mod URLs + ROM hash), for a
  // joiner's session-info request (session-info.ts). Called once by main.ts with
  // a closure over the primary host's tenants + loaded ROM; the PeerHost invokes
  // it lazily when a joiner asks, so it always reflects the CURRENT game (post-
  // hop), not a load-time value.
  setSessionInfoProvider(provider: () => HostSessionInfo): void {
    this.sessionInfoProvider = provider;
  }

  // Whether the shared game can be shared right now: a live in-page server half
  // must actually serve networked clients (listen on a channel). A cosmetic /
  // client-only mod (e.g. "no music") brings up an inert server half with no
  // channels, so hosting isn't offered for it — sharing it would connect a
  // friend to a server with nothing to sync. In plain remote play the local
  // server is empty, so it's off there too.
  canHost(): boolean {
    return this.server !== null && this.server.hasNetworkedMember;
  }

  isHosting(): boolean {
    return this.brokerSession !== null;
  }

  // Live remote-peer count (0 when not sharing) — for the share UI.
  peerCount(): number {
    return this.peerHost?.peerCount ?? 0;
  }

  // Start sharing the shared game to remote peers over WebRTC via the broker.
  // Resolves with the broker session id (for the share link). The router already
  // owns the server sink and serves the couch tiles; this just opens the broker
  // door so remote joiners can take slots alongside them. Throws if already
  // sharing or if there's nothing to host.
  async startHosting(
    brokerUrl: string,
    deps: { brokerDeps?: BrokerSignalDeps } = {},
  ): Promise<string> {
    if (this.brokerSession) throw new Error("already hosting");
    if (!this.peerHost || !this.canHost()) {
      throw new Error("nothing to host: no in-page server half is running");
    }
    const peerHost = this.peerHost;
    const session = new BrokerHostSession(
      brokerUrl,
      (offer) => peerHost.acceptOffer(offer),
      deps.brokerDeps ?? {},
    );
    this.brokerSession = session;
    try {
      return await session.session;
    } catch (e) {
      // Registration failed — unwind so the UI can retry cleanly.
      this.stopHosting();
      throw e;
    }
  }

  // Open an info-ONLY broker session: register with the broker and answer each
  // joiner's session-info bootstrap from the live provider, hosting NO game. For
  // a player on a shared server inviting friends to play alongside them on that
  // server — there's no in-page LocalServer to run (canHost() is false), so this
  // needs neither the server nor the PeerHost, just the broker door + provider.
  // Resolves with the broker session id for the ?join= link. Throws if a session
  // (of either kind) is already open.
  async startInfoHosting(
    brokerUrl: string,
    deps: { brokerDeps?: BrokerSignalDeps } = {},
  ): Promise<string> {
    if (this.brokerSession) throw new Error("already hosting");
    const session = new BrokerHostSession(
      brokerUrl,
      (offer) => acceptInfoOffer(offer, () => this.sessionInfoProvider(), this.peerDeps),
      deps.brokerDeps ?? {},
    );
    this.brokerSession = session;
    try {
      return await session.session;
    } catch (e) {
      this.stopHosting();
      throw e;
    }
  }

  // Stop sharing: close the broker session and every remote peer. The couch
  // tiles' loopbacks stay connected (PeerHost.close closes peers only), so local
  // play continues uninterrupted.
  stopHosting(): void {
    this.brokerSession?.close();
    this.brokerSession = null;
    this.peerHost?.close();
  }

  // Tear the local world down at a fresh-game boundary: stop sharing, stop the
  // server (halting its tick + dropping its members), and clear the memoized
  // construction so the next launch builds a clean one. The shared KV singleton
  // is NOT closed (it outlives any one game). Idempotent.
  reset(): void {
    this.stopHosting();
    this.server?.stop();
    this.server = null;
    this.serverInit = null;
    this.peerHost = null;
  }
}
